import React, { useState, useMemo, useEffect } from 'react';
import {
    Card,
    Divider,
    Grid,
    Button,
    CardContent,
    TextField,
    useMediaQuery,
    Box,
    IconButton,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    FormHelperText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CustomBreadcrumbs from "components/Breadcrumbs";
import Loader from 'components/Loader';
import axiosInstance from "configs/axiosConfig";
import { APIRouteConstants } from "constants/routeConstants";
import { useSnackbar } from 'notistack';
import _ from 'lodash';

const QRCode = ({ breadcrumbs }) => {
    const [loader, setLoader] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [isEditable, setIsEditable] = useState(true);

    const [quantity, setQuantity] = useState('');
    const [number, setNumber] = useState('');
    const [qrPurpose, setQrPurpose] = useState('');

    const [quantityError, setQuantityError] = useState('');
    const [numberError, setNumberError] = useState('');

    const [singleQRGenerated, setSingleQRGenerated] = useState(false);
    const [multipleQRGenerated, setMultipleQRGenerated] = useState(false);
    const [qrdata, setqrdata] = useState({});

    const { enqueueSnackbar } = useSnackbar();
    const isSmallScreen = useMediaQuery('(max-width:600px)');

    const validateQuantity = (val) => {
        if (!val || isNaN(val)) {
            setQuantityError("Quantity must be a number");
            return false;
        }
        setQuantityError('');
        return true;
    };

    const validateNumber = (val) => {
        if (!val || isNaN(val)) {
            setNumberError("Number must be a number");
            return false;
        }
        setNumberError('');
        return true;
    };

    const handleQuantityChange = (e) => {
        const val = e.target.value;
        setQuantity(val);
        validateQuantity(val);
        setMultipleQRGenerated(false);
    };

    const handleNumberChange = (e) => {
        const val = e.target.value;
        setNumber(val);
        validateNumber(val);
        setMultipleQRGenerated(false);
    };

    const handleQrPurposeChange = (e) => {
        setQrPurpose(e.target.value);
        setMultipleQRGenerated(false);
    };

    const createSingleQR = async () => {
        setLoader(true);
        let res = await axiosInstance.post(APIRouteConstants.STORE.CREATEQR, {});
        if (res === false) return setLoader(false);

        let { data, response } = res;
        if (response?.status === 400 && response.data) {
            for (let i in response.data) {
                enqueueSnackbar(_.capitalize(i) + ': ' + response.data[i], { variant: 'error' });
            }
        }

        if (data) {
            enqueueSnackbar('QR Code Generated Successfully', { variant: 'success' });
            setSingleQRGenerated(true);
        }
        setLoader(false);
    };

    const createMultipleQR = async () => {
        const isQtyValid = validateQuantity(quantity);
        const isNumValid = validateNumber(number);
        if (!isQtyValid || !isNumValid) return;

        setLoader(true);
        const res = await axiosInstance.post(APIRouteConstants.STORE.CREATEQR, {
            scenario: "BULK",
            quantity,
            number,
            qr_purpose: qrPurpose,
        });
        if (res === false) return setLoader(false);

        const { data, response } = res;
        if (response?.status === 400 && response.data) {
            for (let i in response.data) {
                enqueueSnackbar(_.capitalize(i) + ': ' + response.data[i], { variant: 'error' });
            }
        }

        if (data) {
            enqueueSnackbar('QR Code Generated Successfully', { variant: 'success' });
            setMultipleQRGenerated(true);
            setIsEditable(false);
        }
        setLoader(false);
    };

    const downloadQR = async (bulk = false) => {
        setDownloading(true);
        try {
            const res = await axiosInstance.get(APIRouteConstants.STORE.QRDOWNLOAD, {
                responseType: 'blob',
                params: bulk ? { scenario: "BULK" } : {},
            });

            if (res?.data) {
                const blob = new Blob([res.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'qrcode.pdf';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                enqueueSnackbar('PDF Downloaded Successfully', { variant: 'success' });
            } else {
                enqueueSnackbar('Failed to download PDF', { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Error downloading PDF', { variant: 'error' });
        }
        setDownloading(false);
    };

    const handleEditClick = () => {
        setIsEditable(true);
    };

    const getqrdata = async () => {
        let res = await axiosInstance.get(APIRouteConstants.STORE.EXISTINGQR)
        if (res === false) {
          setLoader(false);
          return
        }
    
        let { data, response } = res;
    
        if (data) {
            setqrdata(data)
        }
      }

      useEffect(() => {
        getqrdata();
      }, [])
      

    const isMultipleQRButtonDisabled = useMemo(() => {
        return !(quantity && number && qrPurpose && !quantityError && !numberError);
    }, [quantity, number, qrPurpose, quantityError, numberError]);

    return (
        <div className="store-analytics-wrapper website-analytics-wrapper">
            <Loader open={loader || downloading} />
            <div className='breadcrumb-wrapper'>
                <CustomBreadcrumbs list={breadcrumbs} name="Generate QR Code(s)" />
            </div>

            <Grid container className='component-grid'>
                <Grid item xs={12}>
                    <Card className='list-wrapper dashboard-card-wrapper'>
                        <CardContent>
                            <div className='header-wrapper header-flex'>
                                <span className='header-text'>Generate a QR Code for Your Website and Services</span>
                            </div>
                            <Divider />
                            <div className='content-wrapper content-flex'>
                                <span className='content-text'>
                                Easily generate a custom QR code for your website and services! Share it on social media, print it on flyers, or display it at your store for quick customer access. Just click on “Generate QR Code”, and we’ll create a scannable QR code instantly                                </span>
                            </div>

                            {/* SINGLE QR */}
                            <Box sx={{ backgroundColor: 'rgba(100, 100, 100, 0.1)', p: 2, mb: 2 }}>
                                <Grid container justifyContent="space-between" alignItems="center">
                                    <Grid item>
                                        <span className='header-text'>Generate Single QR Code</span>
                                    </Grid>
                                    {/* <Grid item> */}
                                        <Button
                                            variant="contained"
                                            style={{ backgroundColor: singleQRGenerated&& "green", color: 'white', width: '17%', fontSize: '0.65rem'  }}
                                            onClick={() => singleQRGenerated ? downloadQR(false) : createSingleQR()}
                                        >
                                            {singleQRGenerated ? "Download Now" : "Generate Now"}
                                        </Button>
                                    {/* </Grid> */}
                                </Grid>
                            </Box>

                            {/* MULTIPLE QR */}
                            {/* <Box sx={{ backgroundColor: 'rgba(100, 100, 100, 0.1)', p: 2, mt: 2, mb: 3 }}> */}
                            
                            {/* <Grid item>
                                        <span className='header-text'>Existing QR Codes</span>
                                    </Grid> */}
                                    {/* <Box sx={{ backgroundColor: 'rgba(100, 100, 100, 0.1)', p: 2, mb: 2 }}> */}
                                    {Array.isArray(qrdata) && qrdata.length > 0 && (
                                    // <>
                                    <Box sx={{ backgroundColor: 'rgba(100, 100, 100, 0.1)', p: 2, mt: 2 }}>
                                        <Grid container justifyContent="space-between" alignItems="center">
                                        <Grid item>
                                            {/* <span className='header-text'>Existing QR Codes</span> */}
                                            <span className='header-text'>Download Existing QR Codes</span>
                                        </Grid>
                                        {/* <Grid item> */}
                                            <Button
                                            variant="contained"
                                            style={{ backgroundColor: "green", color: 'white', width: '17%', fontSize: '0.65rem' }}
                                            onClick={() => downloadQR(true)}
                                            >
                                            Download AGAIN
                                            </Button>
                                        {/* </Grid> */}
                                        </Grid>

                                        <Grid container spacing={0.5} sx={{ mt: 1, mb: 2, fontSize: '0.7rem', color: 'grey' }}>
                                        <Grid item xs={12}>
                                            • Your existing QR codes are for <b>{qrdata[0]?.label?.toLowerCase() || 'table/room'} numbers</b> {' '}
                                            <b>{'<'}{qrdata.map(item => item.number).join(', ')}{'>'}</b>
                                        </Grid>
                                        <Grid item xs={12}>
                                            • You can download the existing QR codes for use by clicking on download now. 
                                        </Grid>
                                        <Grid item xs={12}>
                                            <b>Please note:</b>  If you wish to generate new QR codes all previously generated QR codes will expire and be replaced automatically.
                                        </Grid>
                                        </Grid>
                                    {/* </> */} 
                                    </Box>
                                    )}

                            {/* </Box> */}
                            <Box sx={{ backgroundColor: 'rgba(100, 100, 100, 0.1)', p: 2, mt: 2, mb: 3 }}>
                                <Grid container justifyContent="space-between" alignItems="center">
                                    <Grid item>
                                        <span className='header-text'>Generate New Multiple QR Codes</span>
                                    </Grid>
                                    {multipleQRGenerated && (
                                        <Grid item>
                                            <IconButton onClick={handleEditClick} size="small">
                                                <EditIcon />
                                            </IconButton>
                                        </Grid>
                                    )}
                                </Grid>
                                

                                {/* <Grid container spacing={0.5} sx={{ mt: 1, mb: 2, fontSize: '0.7rem' }} style={{color: "grey"}}>
                                    <Grid item xs={12} style={{fontSize: "17px"}}>To generate multiple QR codes, please enter the following details:<br /></Grid>
                                    
                                    <Grid item xs={12}>• Quantity – The number of QR codes you want to generate.(For eg: 100)</Grid>
                                    <Grid item xs={12}>• Number – Please enter the starting number for your QR codes. For example, if you start with 100, the codes will be generated from 100 onwards. If your hotel room or restaurant table numbers begin at 401, enter 401</Grid>
                                    <Grid item xs={12}>• QR Purpose – Select "Room" or "Table" to specify what the QR codes are for, like hotel rooms or restaurant tables</Grid>
                                </Grid> */}
                                <Grid container spacing={0.5} sx={{ mt: 1, mb: 2, fontSize: '0.7rem', color: 'grey' }}>
                                <Grid item xs={12} sx={{ fontSize: "17px", mb: 1 }}>
                                    To generate multiple QR codes, please enter the following details:
                                </Grid>

                                <Grid item xs={12}>
                                    • Quantity – The number of QR codes you want to generate. (For eg: 100)
                                </Grid>
                                <Grid item xs={12}>
                                    • Number – Please enter the starting number for your QR codes. For example, if you start with 100, the codes will be generated from 100 onwards. If your hotel room or restaurant table numbers begin at 401, enter 401
                                </Grid>
                                <Grid item xs={12}>
                                    • QR Purpose – Select "Room" or "Table" to specify what the QR codes are for, like hotel rooms or restaurant tables
                                </Grid>
                                </Grid>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Quantity"
                                            variant="outlined"
                                            value={quantity}
                                            onChange={handleQuantityChange}
                                            disabled={!isEditable}
                                            error={!!quantityError}
                                            helperText={quantityError}
                                            InputProps={{ readOnly: !isEditable }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Starting Number"
                                            variant="outlined"
                                            value={number}
                                            onChange={handleNumberChange}
                                            disabled={!isEditable}
                                            error={!!numberError}
                                            helperText={numberError}
                                            InputProps={{ readOnly: !isEditable }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth error={!qrPurpose && !isEditable}>
                                            <InputLabel>QR Purpose</InputLabel>
                                            <Select
                                                value={qrPurpose}
                                                onChange={handleQrPurposeChange}
                                                label="QR Purpose"
                                                disabled={!isEditable}
                                            >
                                                <MenuItem value="Room">Room</MenuItem>
                                                <MenuItem value="Table">Table</MenuItem>
                                            </Select>
                                            {/* {!qrPurpose && isEditable && <FormHelperText>Required</FormHelperText>} */}
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Box mt={3} display="flex" justifyContent="space-around">
    {multipleQRGenerated && !isEditable ? (
        <Button
            variant="contained"
            style={{ backgroundColor: "green", color: 'white', width: '17%', fontSize: '0.65rem' }} // Set width
            onClick={() => downloadQR(true)}
        >
            Download Now
        </Button>
    ) : (
        <Button
            variant="contained"
            style={{ backgroundColor: 'black', color: 'white', width: '17%', fontSize: '0.65rem' }} // Set width
            onClick={createMultipleQR}
            disabled={isMultipleQRButtonDisabled}
        >
            Generate Now
        </Button>
    )}
</Box>
                                </Box>
                            {/* </Box> */}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </div>
    );
};

export default QRCode;
